import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Menu, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import LanguageSwitcher from '@/components/LanguageSwitcher';

// Flat, one-row nav grew to 11 items + the language switcher as sections were
// added — cramped on desktop and wrapped to two lines in longer languages.
// Grouped into two dropdowns by intent (personal/credentials vs. portfolio
// work) so the bar always reads as 5 top-level destinations.
const TOP_LEVEL_IDS = ['hero'] as const;
const ABOUT_GROUP_IDS = ['about', 'skills', 'certificate'] as const;
const WORK_SECTION_IDS = ['projects', 'websites', 'designs', 'videos', 'extensions'] as const;
const TAIL_IDS = ['services', 'contact'] as const;

const NAV_LABEL_KEYS: Record<string, string> = {
  hero: 'nav.home',
  about: 'nav.overview',
  skills: 'nav.skills',
  certificate: 'nav.certificates',
  projects: 'nav.projects',
  websites: 'nav.websites',
  designs: 'nav.designs',
  videos: 'nav.videos',
  extensions: 'nav.extensions',
  services: 'nav.services',
  contact: 'nav.contact',
};

const Navbar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-card/80 backdrop-blur-lg shadow-card' : 'bg-transparent'}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="text-xl font-bold bg-gradient-primary bg-clip-text text-transparent">
            Ahmad Abid
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            {TOP_LEVEL_IDS.map((id) => (
              <button key={id} onClick={() => scrollToSection(id)} className="text-foreground hover:text-primary transition-colors">
                {t(NAV_LABEL_KEYS[id])}
              </button>
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1 text-foreground hover:text-primary transition-colors outline-none">
                {t('nav.about')} <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {ABOUT_GROUP_IDS.map((id) => (
                  <DropdownMenuItem key={id} onClick={() => scrollToSection(id)}>
                    {t(NAV_LABEL_KEYS[id])}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1 text-foreground hover:text-primary transition-colors outline-none">
                {t('nav.work')} <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {WORK_SECTION_IDS.map((id) => (
                  <DropdownMenuItem key={id} onClick={() => scrollToSection(id)}>
                    {t(NAV_LABEL_KEYS[id])}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link to="/templates">{t('nav.appTemplates')}</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {TAIL_IDS.map((id) => (
              <button key={id} onClick={() => scrollToSection(id)} className="text-foreground hover:text-primary transition-colors">
                {t(NAV_LABEL_KEYS[id])}
              </button>
            ))}
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-foreground p-2 -mr-2"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu — a vertical list doesn't suffer the "too many things
            in one row" problem the dropdowns above solve, so it stays flat;
            just grouped in the same order with light section labels. */}
        {isOpen && (
          <div className="lg:hidden pb-4 flex flex-col gap-1 bg-card rounded-b-xl shadow-card -mx-4 px-4">
            {TOP_LEVEL_IDS.map((id) => (
              <button key={id} onClick={() => scrollToSection(id)} className="text-foreground hover:text-primary transition-colors text-left py-2.5">
                {t(NAV_LABEL_KEYS[id])}
              </button>
            ))}

            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-3 pb-1">{t('nav.about')}</p>
            {ABOUT_GROUP_IDS.map((id) => (
              <button key={id} onClick={() => scrollToSection(id)} className="text-foreground hover:text-primary transition-colors text-left py-2.5">
                {t(NAV_LABEL_KEYS[id])}
              </button>
            ))}

            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-3 pb-1">{t('nav.work')}</p>
            {WORK_SECTION_IDS.map((id) => (
              <button key={id} onClick={() => scrollToSection(id)} className="text-foreground hover:text-primary transition-colors text-left py-2.5">
                {t(NAV_LABEL_KEYS[id])}
              </button>
            ))}
            <Link to="/templates" className="text-foreground hover:text-primary transition-colors text-left py-2.5" onClick={() => setIsOpen(false)}>
              {t('nav.appTemplates')}
            </Link>

            <div className="pt-3 border-t border-border mt-2" />
            {TAIL_IDS.map((id) => (
              <button key={id} onClick={() => scrollToSection(id)} className="text-foreground hover:text-primary transition-colors text-left py-2.5">
                {t(NAV_LABEL_KEYS[id])}
              </button>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
